import { useState } from 'react';

import { useDirectTradingAccountSecurityPricePerformance } from './hooks/useDirectTradingAccountSecurityPricePerformance';
import { PerformanceLoadingState } from './loading-state/PerformanceLoadingState';
import { PerformanceSection } from './PerformanceSection';

import { trackClick } from '../../../../../features/track';
import { DateRangeOption } from '../../../../../graphql/schema';

type PerformanceChartSectionProps = {
  accountId: string;
  securityId: string;
  currentDate: Date;
};

export const PerformanceChartSection: React.FC<PerformanceChartSectionProps> = ({
  accountId,
  securityId,
  currentDate,
}) => {
  const [dateRangeOption, setDateRangeOption] = useState<DateRangeOption>(DateRangeOption.Today);

  const {
    data: newData,
    loading,
    previousData,
  } = useDirectTradingAccountSecurityPricePerformance(accountId, securityId, currentDate, dateRangeOption);

  const handleDateRangeChange = (option: DateRangeOption) => {
    trackClick({ name: 'DateRangeInteraction' });
    setDateRangeOption(option);
  };

  const data = newData ?? previousData;
  const hasValidData = data && 'financialSecurity' in data.account;
  const directTradingAccount = hasValidData ? data.account : null;

  if (loading && !data) return <PerformanceLoadingState />;
  if (!hasValidData || !directTradingAccount) return null;

  const { marketSchedule } = data;
  const { financialSecurity } = directTradingAccount;
  const { pricePerformance } = financialSecurity;
  const { timeSeries } = pricePerformance;
  const { returns } = pricePerformance;
  const { statistics } = financialSecurity;
  const { latestPrice } = statistics;

  const isLoadingWithPreviousData = loading && !newData && !!previousData;

  return (
    <PerformanceSection
      latestPrice={latestPrice}
      returns={returns}
      timeSeries={timeSeries}
      isLoading={isLoadingWithPreviousData}
      marketSchedule={marketSchedule}
      dateRangeOption={dateRangeOption}
      currentDate={currentDate}
      onDateRangeChange={handleDateRangeChange}
    />
  );
};
